import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause } from "lucide-react";

const SLIDES = [
  { src: "/shots/dashboard.png", title: "Dashboard", caption: "Role-aware KPIs, activity and training completion at a glance." },
  { src: "/shots/rota.png", title: "Calendar & ROTA", caption: "Month-grid rota with blockable slots and scheduled work." },
  { src: "/shots/people.png", title: "People directory", caption: "Search and filter the whole workforce by region, role and manager." },
  { src: "/shots/templates.png", title: "Editable templates", caption: "Microsoft Forms-style builder for reviews and assessments." },
];

const DURATION = 3800;

export const Walkthrough = () => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setInterval(() => setI((p) => (p + 1) % SLIDES.length), DURATION);
    return () => clearInterval(timer.current);
  }, [playing]);

  const go = (idx) => {
    setI(idx);
    clearInterval(timer.current);
    if (playing) timer.current = setInterval(() => setI((p) => (p + 1) % SLIDES.length), DURATION);
  };

  const active = SLIDES[i];

  return (
    <section className="bg-[#0F172A]" data-testid="landing-walkthrough">
      <div className="mx-auto max-w-6xl px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">Product tour</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            A quick look inside ESP26.
          </h2>
          <p className="mt-3 text-slate-400">Real screens, auto-playing — no login required.</p>
        </div>

        <div className="relative mx-auto mt-12 max-w-5xl">
          {/* browser chrome */}
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-2xl">
            <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.04] px-4 py-3">
              <span className="h-3 w-3 rounded-full bg-red-400/70" />
              <span className="h-3 w-3 rounded-full bg-amber-400/70" />
              <span className="h-3 w-3 rounded-full bg-emerald-400/70" />
              <span className="ml-3 truncate rounded-md bg-white/5 px-3 py-1 text-xs text-slate-400">
                app.esp26.uk{i === 0 ? "/dashboard" : i === 1 ? "/calendar" : i === 2 ? "/people" : "/templates"}
              </span>
            </div>
            <div className="relative aspect-[1440/900] w-full bg-slate-950">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active.src}
                  src={active.src}
                  alt={active.title}
                  data-testid="walkthrough-image"
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.99 }}
                  transition={{ duration: 0.55, ease: "easeOut" }}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </AnimatePresence>
            </div>
          </div>

          {/* caption + controls */}
          <div className="mt-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="text-center sm:text-left">
              <p className="font-heading text-lg font-bold">{active.title}</p>
              <p className="text-sm text-slate-400">{active.caption}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="walkthrough-playpause"
                aria-label={playing ? "Pause tour" : "Play tour"}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-slate-200 transition-colors hover:bg-white/10"
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <div className="flex items-center gap-2">
                {SLIDES.map((s, idx) => (
                  <button
                    key={s.src}
                    onClick={() => go(idx)}
                    data-testid={`walkthrough-dot-${idx}`}
                    aria-label={`Show ${s.title}`}
                    className="group h-2 overflow-hidden rounded-full bg-white/15 transition-all"
                    style={{ width: idx === i ? 40 : 12 }}
                  >
                    {idx === i && playing && (
                      <motion.span
                        key={`${i}-bar`}
                        className="block h-full bg-orange-500"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: DURATION / 1000, ease: "linear" }}
                      />
                    )}
                    {idx === i && !playing && <span className="block h-full w-full bg-orange-500" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
